/**
 * VatConfirmationBadge — WL-K12 UI-Unterscheidung `confirmed_qualified` vs.
 * `confirmed_simple` (architecture-loop-b.md §4.8, `functions/src/moat/
 * vatIdValidation.ts`).
 *
 * §18e-Bestätigungen kommen serverseitig in vier neutralen Status-Werten
 * zurück (Wording-Guard, s. `vatIdValidation.ts` Modul-Kopf). VORHER zeigte
 * das Frontend `confirmed_qualified` (Name/Adresse abgeglichen, Art. 31 VO
 * (EU) 904/2010, Stufe 2) und `confirmed_simple` (nur Gültigkeit, Stufe 1)
 * identisch an — diese Komponente macht den Unterschied sichtbar: ein
 * kräftiges grünes Siegel für die qualifizierte Bestätigung, ein dezenteres
 * neutrales Badge für die einfache Bestätigung. `invalid`/`unconfirmed`
 * werden ebenfalls neutral/negativ dargestellt (kein grünes „bestätigt" bei
 * Unsicherheit — spiegelt das server-seitige Fail-closed-Prinzip optisch).
 *
 * WORDING (⚠️-light [JURIST], wie `vatIdValidation.ts`): rein dokumentierend
 * ("bestätigt"/"ungültig"/"nicht bestätigt") — NIE ein Steuerfrei-/
 * Vorsteuerabzugsfähig-Claim.
 *
 * Rein präsentational — kein `useAuth`, kein Firestore-/Callable-Zugriff. Der
 * Aufrufer berechnet Sichtbarkeit (Beta-Gate) und liefert nur den Status.
 *
 * @module VatConfirmationBadge
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconSuccess, IconCheckDouble, IconWarning, IconHelp } from '../../icons';
import type { VatConfirmationStatus } from '../../api/vatConfirmationApi';

interface VatConfirmationBadgeProps {
  /** Bestätigungs-Status, oder `null`/`undefined` (noch keine Prüfung ⇒ kein Badge). */
  status: VatConfirmationStatus | null | undefined;
}

const VatConfirmationBadge: React.FC<VatConfirmationBadgeProps> = ({ status }) => {
  const { t } = useTranslation('posteingang');

  if (!status) return null;

  if (status === 'confirmed_qualified') {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full bg-[var(--tone-success-bg)] border border-emerald-500/40 px-2.5 py-1 text-xs font-semibold text-[var(--tone-success-fg)]"
        title={t(
          'vatCheck.qualifiedTooltip',
          'Name und Adresse wurden mit der amtlichen Bestätigung abgeglichen (Art. 31 VO (EU) 904/2010, Stufe 2).',
        )}
      >
        <IconCheckDouble className="text-[var(--tone-success-fg)]" size={12} />
        {t('vatCheck.qualified', 'Qualifiziert bestätigt (Name/Adresse abgeglichen)')}
      </span>
    );
  }

  if (status === 'confirmed_simple') {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full bg-slate-700/40 border border-slate-500/40 px-2.5 py-1 text-xs font-medium text-slate-300"
        title={t(
          'vatCheck.simpleTooltip',
          'Nur die Gültigkeit der USt-IdNr. wurde bestätigt — Name und Adresse wurden NICHT abgeglichen (Stufe 1).',
        )}
      >
        <IconSuccess className="text-slate-400" size={12} />
        {t('vatCheck.simple', 'Einfach bestätigt (nur Gültigkeit)')}
      </span>
    );
  }

  if (status === 'invalid') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--tone-danger-bg)] border border-red-500/40 px-2.5 py-1 text-xs font-medium text-[var(--tone-danger-fg)]">
        <IconWarning className="text-[var(--tone-danger-fg)]" size={12} />
        {t('vatCheck.invalid', 'Ungültig')}
      </span>
    );
  }

  // 'unconfirmed'
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-[rgb(var(--surface-inset-rgb)/0.4)] border border-strong px-2.5 py-1 text-xs font-medium text-muted">
      <IconHelp className="text-faint" size={12} />
      {t('vatCheck.unconfirmed', 'Nicht bestätigt')}
    </span>
  );
};

export default VatConfirmationBadge;
